import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import {
  fetchProgressReportPdf,
  getCoachPortfolio,
  type CoachPortfolioData,
  type PortfolioMetricData,
} from '@/lib/api-client';

/**
 * DSH-144 — Coach Quick View (Mobile-Responsive).
 *
 * Coach checks client health on phone between sessions. The layout is a
 * card list (one card per client) that stays single-column on phones
 * (375px+) and expands to 2 columns on tablets / 3 on desktops via
 * Tailwind's responsive breakpoints.
 *
 * Surfaces:
 *   - Active count + total tile at top
 *   - Attention queue (top-3 needs-attention) as a separate row of cards
 *     with a red/yellow/green tone bar
 *   - Full list of portfolio metrics, sortable by attention (default)
 *   - Each card: name + step badge + traffic-light + 7-day fulfillment
 *     average + "Missed N days" attention chip when relevant
 *
 * Read-only by design — no tool editing on mobile (per AC). Tapping a
 * card on desktop scrolls/links into the existing `/clients` page; on
 * mobile we keep it as a non-interactive view to stay focused.
 *
 * @see DSH-144 Coach Quick View (Mobile-Responsive)
 */
export function CoachQuickViewPage(): JSX.Element {
  const { user, accessToken } = useAuth();
  const { t } = useTranslation();

  const [data, setData] = useState<CoachPortfolioData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCoachLike = user?.role === 'coach' || user?.role === 'head_coach';
  const coachId = user?.coachId ?? null;

  useEffect(() => {
    if (!user || !coachId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCoachPortfolio(accessToken, coachId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, coachId]);

  if (!isCoachLike) {
    return (
      <div className="mx-auto max-w-prose p-8 text-sm text-ink-secondary">
        {t('coachQuickView.notAuthorized')}
      </div>
    );
  }

  if (!coachId) {
    return (
      <div className="mx-auto max-w-prose p-8 text-sm text-ink-secondary">
        {t('coachQuickView.noCoachRow')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      <header>
        <p className="text-xs uppercase tracking-wide text-ink-secondary">
          {t('coachQuickView.eyebrow')}
        </p>
        <h1 className="font-display text-2xl text-ink-primary sm:text-3xl">
          {t('coachQuickView.title')}
        </h1>
      </header>

      {loading && (
        <p className="text-sm text-ink-secondary">{t('coachQuickView.loading')}</p>
      )}
      {error && (
        <div
          className="rounded-md border border-flame bg-flame-glow p-3 text-sm text-flame-ember"
          data-testid="coach-quick-view-error"
        >
          {error}
        </div>
      )}

      {data && (
        <>
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <SummaryTile
              label={t('coachQuickView.summary.active')}
              value={String(data.activeCount)}
            />
            <SummaryTile
              label={t('coachQuickView.summary.total')}
              value={String(data.totalCount)}
            />
            <SummaryTile
              label={t('coachQuickView.summary.needsAttention')}
              value={String(data.attentionQueue.length)}
              tone={data.attentionQueue.length > 0 ? 'red' : 'green'}
            />
          </section>

          {data.attentionQueue.length > 0 && (
            <section data-testid="attention-queue">
              <h2 className="mb-2 text-sm font-semibold text-ink-primary">
                {t('coachQuickView.attentionQueue.title')}
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.attentionQueue.map((m) => (
                  <ClientCard key={m.clientId} m={m} t={t} compact />
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-2 text-sm font-semibold text-ink-primary">
              {t('coachQuickView.all.title', { count: data.metrics.length })}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.metrics.map((m) => (
                <ClientCard key={m.clientId} m={m} t={t} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'green' | 'yellow' | 'red';
}): JSX.Element {
  const toneBg =
    tone === 'red'
      ? 'bg-flame-glow'
      : tone === 'yellow'
        ? 'bg-amber-100'
        : 'bg-parchment-raised';
  return (
    <div className={`rounded-md border border-parchment-rule p-3 text-center ${toneBg}`}>
      <strong className="block text-2xl text-ink-primary">{value}</strong>
      <span className="text-xs text-ink-secondary">{label}</span>
    </div>
  );
}

function ClientCard({
  m,
  t,
  compact = false,
}: {
  m: PortfolioMetricData;
  t: ReturnType<typeof useTranslation>['t'];
  compact?: boolean;
}): JSX.Element {
  const { accessToken, user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const toneColor =
    m.tone === 'red'
      ? 'bg-rose-500'
      : m.tone === 'yellow'
        ? 'bg-amber-400'
        : 'bg-emerald-500';
  const fulfillmentLabel =
    m.fulfillment7dAvg === null
      ? t('coachQuickView.metric.noData')
      : `${m.fulfillment7dAvg}/100`;

  const handleDownload = async (): Promise<void> => {
    if (!user || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await fetchProgressReportPdf(accessToken, m.clientId);
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.href = url;
      link.download = `h3eromap-progress-${m.clientId}-${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="overflow-hidden rounded-md border border-parchment-rule bg-parchment"
      data-testid={`client-card-${m.clientId}`}
      data-tone={m.tone}
    >
      <div className={`h-1.5 ${toneColor}`} aria-hidden />
      <div className="space-y-1 p-3">
        <div className="flex items-baseline justify-between">
          <strong className="text-sm text-ink-primary">{m.fullName}</strong>
          <span className="text-xs text-ink-secondary">
            {t('coachQuickView.metric.step', { step: m.currentStep, phase: m.currentPhase })}
          </span>
        </div>
        <p className="text-xs text-ink-secondary">
          {t('coachQuickView.metric.fulfillment7d')}: <strong>{fulfillmentLabel}</strong>
        </p>
        {!compact && m.missedTrackingDays > 0 && (
          <p className="text-xs text-flame-ember">
            {t('coachQuickView.metric.missedDays', { count: m.missedTrackingDays })}
          </p>
        )}
        {!compact && m.consecutiveFallbackDays > 0 && (
          <p className="text-xs text-flame-ember">
            {t('coachQuickView.metric.fallbackDays', { count: m.consecutiveFallbackDays })}
          </p>
        )}
        {!compact && m.fulfillmentDrop > 0 && (
          <p className="text-xs text-flame-ember">
            {t('coachQuickView.metric.drop', { delta: m.fulfillmentDrop.toFixed(1) })}
          </p>
        )}
        {!compact && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => {
                void handleDownload();
              }}
              disabled={downloading || !user}
              className="inline-flex items-center gap-1 text-xs text-flame-ember underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`progress-report-download-${m.clientId}`}
              aria-label={t('coachQuickView.metric.progressReport.aria', { name: m.fullName })}
            >
              {downloading
                ? t('coachQuickView.metric.progressReport.downloading')
                : t('coachQuickView.metric.progressReport.label')}
            </button>
            {downloadError && (
              <p className="mt-1 text-xs text-rose-700" role="alert">
                {downloadError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
